import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { sendGAEvent } from "../../services/AnalyticsService";
import {
    GA_BUTTON_EVENT,
    GA_CLICK_LLI_CASUALTIES_LINK
} from "../../constants/GaEvents";
import {
    LliLinkButton,
    SeaSearcherLinkButton
} from "@idwal/idwal-react-components";
import { Timeline } from "primereact/timeline";
import { Button } from "primereact/button";
import { useParams } from "react-router-dom";
import {
    selectIncidentsByIMO,
    selectIncidentsError,
    selectIncidentsLoading
} from "../../features/screeningSlice";
import { useAppSelector } from "../../redux/hooks";
import { Loading } from "../loading/Loading";
import { ScreeningCasualty } from "../../types/Screening";
import dayjs from "dayjs";
import "./ScreeningIncidents.css";

export const ScreeningIncidents = () => {
    const { imo } = useParams();
    const { t } = useTranslation("locale");
    const incidentsData = selectIncidentsByIMO(imo);
    const loading = useAppSelector(selectIncidentsLoading);
    const error = useAppSelector(selectIncidentsError);

    const gaEvent = useCallback(() => {
        sendGAEvent(GA_BUTTON_EVENT, GA_CLICK_LLI_CASUALTIES_LINK);
    }, []);

    if (loading) {
        return <Loading />;
    }

    const itemTemplate = (item: ScreeningCasualty) => {
        return (
            <div className="incidents-timeline-box mb-3 flex flex-column md:flex-row gap-3">
                <div className="flex gap-3 md:contents">
                    <span className="incidents-text w-7rem">
                        {dayjs(item.casualtyDate).format("DD/MM/YYYY")}
                    </span>
                    <span className="incidents-timeline-type w-7rem">
                        {item.casualtyType}
                    </span>
                </div>
                <div className="flex-1">
                    <div className="incidents-text">
                        {[item.cause1, item.cause2, item.cause3]
                            .filter(Boolean)
                            .join(", ")}
                    </div>
                    <div className="incidents-timeline-location mt-1">
                        {item.location}
                    </div>
                </div>
                <div className="flex align-items-center">
                    <SeaSearcherLinkButton
                        key={0}
                        vesselId={`${incidentsData?.vesselId}`}
                        gaEvent={gaEvent}
                        route="casualties"
                    >
                        <Button
                            className="incidents-button ml-0 md:ml-6"
                            label={t("incidents.moreDetail")}
                            size="small"
                            outlined
                        />
                    </SeaSearcherLinkButton>
                </div>
            </div>
        );
    };

    const markerTemplate = () => <div className="incidents-timeline-marker" />;

    const pageHeader = (
        <div className="flex justify-content-between">
            <p className="psc-header mb-0">
                {t("incidents.incidentsTimetable_36")}
            </p>
            {incidentsData?.vesselId ? (
                <LliLinkButton
                    vesselId={`${incidentsData?.vesselId ?? 0}`}
                    gaEvent={gaEvent}
                    route="casualties"
                />
            ) : (
                <LliLinkButton />
            )}
        </div>
    );

    const noIncidentsMessage = (
        <div
            className="flex justify-content-center align-items-center h-3rem"
            style={{ backgroundColor: "#FCFCFC" }}
        >
            {t("incidents.noIncidents")}
        </div>
    );

    const errorMessage = (
        <div
            className="flex justify-content-center align-items-center h-3rem"
            style={{ backgroundColor: "#FCFCFC" }}
        >
            {t("incidents.unavailable")}
        </div>
    );

    const messageContent = error ? errorMessage : noIncidentsMessage;
    const sortedCasualties = [...(incidentsData?.casualties ?? [])].sort(
        (a, b) =>
            dayjs(b.casualtyDate).valueOf() - dayjs(a.casualtyDate).valueOf()
    );

    return (
        <>
            {pageHeader}
            {sortedCasualties.length > 0 ? (
                <div className="grid mt-4 pl-3">
                    <div className="col-12 lg:col-12 p-0">
                        <Timeline
                            align="left"
                            className="incidents-timeline"
                            value={sortedCasualties}
                            content={itemTemplate}
                            marker={markerTemplate}
                        />
                    </div>
                </div>
            ) : (
                messageContent
            )}
        </>
    );
};
